import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import SqftJsonApi from "../../../../api/sqftJsonApi";
import {SHAPE_LAYOUTS} from "../../../canvas/core/cabinet/cabinetShapes";

class ModelLoader {
  constructor(scene, modelScale) {
    this.scene = scene;
    this.modelScale = modelScale;
    this.loader = new GLTFLoader();
  }

  async loadModel(cabinetData) {
    if (!cabinetData.url) {
      console.log(`Missing URL for partId: ${cabinetData.partId}`);
      return Promise.resolve(`Missing URL for partId: ${cabinetData.partId}`);
    }

    let urlToLoad = cabinetData.url;
    // Logic for loading resized model
    if (cabinetData.target_width !== cabinetData.width) {
      try {
        let resizedCabinet = await this.getResizedCabinetModel(cabinetData);
        if (resizedCabinet && resizedCabinet.s3Url) {
          urlToLoad = resizedCabinet.s3Url;
        }
      } catch (error) {
        console.error(`Error fetching resized model for partId: ${cabinetData.partId}`, error);
      }
    }

    return new Promise((resolve, reject) => {
      this.loader.load(urlToLoad, async (gltf) => {
        const cabinetScene = gltf.scene;

        gltf.scene.position.set(
          cabinetData.x / this.modelScale,
          cabinetData.y / this.modelScale,
          cabinetData.z / this.modelScale
        );
        gltf.scene.rotation.y = THREE.MathUtils.degToRad(cabinetData.rotation);

        this.scene.add(cabinetScene);

        // Load Material from parent feature
        if (cabinetData.material_url) {
          await this.loadMaterial(cabinetData.material_url, cabinetScene);
        }

        // Load handles
        if (cabinetData.handles && cabinetData.handles.length > 0) {
          await this.loadHandles(cabinetData, cabinetScene);
        }

        resolve(cabinetScene);
      }, undefined, (error) => {
        console.error(`Error loading model from URL ${urlToLoad}:`, error);
        reject(error);
      });
    });
  }

  async loadMaterial(materialUrl, scene) {
    return new Promise((resolve, reject) => {
      this.loader.load(materialUrl, (materialGltf) => {
        scene.traverse((child) => {
          if (child.isMesh) {
            const materialMesh = materialGltf.scene.getObjectByProperty('type', 'Mesh');
            if (materialMesh) {
              child.material = materialMesh.material;
            }
          }
        });
        resolve();
      }, undefined, (error) => {
        console.error(`Error loading material from URL ${materialUrl}:`, error);
        reject(error);
      });
    });
  }

  async loadHandles(cabinetData, cabinetScene) {
    const drawerObjects = [];
    const doorObjects = [];

    // Filtering scene objects by names (we take positions for drawers and doors)
    cabinetScene.traverse((object) => {
      if (object.name && object.name.includes('Drawer_')) {
        drawerObjects.push(object); // Positions for drawers
      } else if (object.name && object.name.includes('Door_')) {
        doorObjects.push(object); // Positions for doors
      }
    });

    if (drawerObjects.length === 0 && doorObjects.length === 0) {
      this.logMeshes();
      console.log(`Handles position not found in the scene`);
      return;
    }

    // Loop through cabinetData handles and link them to the correct positions
    for (const handleIndex in cabinetData.handles) {
      if (cabinetData.handles.hasOwnProperty(handleIndex)
        && cabinetData.handles[handleIndex].handle3dURL // if handle URL exists
      ) {
        const handle = cabinetData.handles[handleIndex];

        // Load the handle model and choose the correct one (horizontal or vertical)
        await this.loadHandle(handle, drawerObjects, doorObjects, cabinetScene);
      }
    }
  }

  async loadHandle(handle, drawerObjects, doorObjects, cabinetScene) {
    return new Promise((resolve, reject) => {
      this.loader.load(handle.handle3dURL, (gltfHandle) => {
        if (!gltfHandle || !gltfHandle.scene) {
          console.error(`GLTF scene not found for URL ${handle.handle3dURL}`);
          reject(new Error('GLTF scene not found'));
          return;
        }

        const children = gltfHandle.scene.children;

        for (let i = 0; i < children.length; i++) {
          const child = children[i];

          if (child.isMesh) {
            // If handle is horizontal (for Drawer)
            if (child.name.includes('_H_')) {
              for (const drawer of drawerObjects) {
                this.applyHandleAsChild(child, handle, drawer, cabinetScene);
              }
            }

            // If handle is vertical (for Door)
            if (child.name.includes('_V_')) {
              for (const door of doorObjects) {
                this.applyHandleAsChild(child, handle, door, cabinetScene);
              }
            }
          }
        }

        resolve(gltfHandle.scene);
      }, undefined, (error) => {
        console.error(`Error loading handle from URL ${handle.handle3dURL}:`, error);
        reject(error);
      });
    });
  }

  // Function to apply the handle to a specific position
  applyHandle(handleMesh, handle, objectPlacement, cabinetScene) {
    const handlePosition = objectPlacement.position.clone();
    const handleRotation = objectPlacement.rotation.clone();
    const cabinetPosition = cabinetScene.position.clone();

    const finalHandlePosition = new THREE.Vector3().addVectors(
      cabinetPosition,
      new THREE.Vector3(
        handlePosition.x,
        handlePosition.y,
        handlePosition.z
      )
    );
    const handleClone = handleMesh.clone(); // Clone the handle to avoid reusing the same object
    handleClone.position.copy(finalHandlePosition);
    handleClone.rotation.copy(handleRotation);

    this.scene.add(handleClone);
  }

  applyHandleAsChild(handleMesh, handle, objectPlacement) {
    const handleClone = handleMesh.clone();

    objectPlacement.add(handleClone);
  }

  async getResizedCabinetModel(position) {
    if (position.shapeLayout !== SHAPE_LAYOUTS.rectangle) {
      return await SqftJsonApi.getResizedCornerCabinet(position, {});
    } else {
      return await SqftJsonApi.getResizedCabinet(position, {});
    }
  }

  drawLineBetweenPoints(start, end) {
    const material = new THREE.LineBasicMaterial({ color: 0xff0000 });
    const points = [];
    points.push(new THREE.Vector3(start.x, start.y, start.z));
    points.push(new THREE.Vector3(end.x, end.y, end.z));

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, material);

    this.scene.add(line);
  }

  logMeshes() {
    const allAssets = [];
    this.scene.traverse((object) => {
      allAssets.push(object);
    });

    console.log(allAssets);
  }
}

export default ModelLoader;
